import React, { useEffect, useState } from 'react';
import { PartyPopper, Play } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from '../../hooks/use-toast';
import api from '../../services/api';

const Row = ({ label, hint, children }) => (
  <div className="flex items-center justify-between gap-4 py-2">
    <div>
      <p className="text-sm font-medium text-gray-800">{label}</p>
      {hint && <p className="text-xs text-gray-500">{hint}</p>}
    </div>
    {children}
  </div>
);

export const WelcomeBackSettingsCard = ({ readOnly }) => {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/api/settings/welcome-back').then((r) => setCfg(r.data.current)).catch(() => {});
  }, []);

  const save = async (patch) => {
    const next = { ...cfg, ...patch };
    setCfg(next);
    try {
      const { data } = await api.put('/api/settings/welcome-back', patch);
      setCfg(data.current);
      toast({ title: 'Saved', description: 'Welcome Back nudge settings updated.' });
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || 'Save failed', variant: 'destructive' });
    }
  };

  const runNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/api/settings/welcome-back/run-now');
      toast({ title: 'Done', description: `${data.sent} welcome-back nudge(s) sent for staff returning today.` });
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || 'Run failed', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  if (!cfg) return null;
  return (
    <Card className="mt-6" data-testid="welcome-back-settings-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2"><PartyPopper className="h-4 w-4 text-sky-600" /> Welcome Back Nudge</CardTitle>
        <p className="text-xs text-gray-500">Tells reporting managers (portal + email, HR copied) when a team member returns from leave. Sick leave and public holidays are skipped.</p>
      </CardHeader>
      <CardContent className="divide-y">
        <Row label="Enabled" hint="Master on/off switch">
          <Switch checked={cfg.enabled} disabled={readOnly} onCheckedChange={(v) => save({ enabled: v })} data-testid="welcome-back-enabled" />
        </Row>
        <Row label="Minimum leave length" hint="Only leaves of at least this many days trigger a nudge">
          <div className="flex items-center gap-2">
            <Input type="number" min={0.5} step={0.5} className="w-20 h-8" disabled={readOnly} value={cfg.min_days}
              onChange={(e) => setCfg({ ...cfg, min_days: e.target.value })}
              onBlur={(e) => Number(e.target.value) > 0 && save({ min_days: Number(e.target.value) })}
              data-testid="welcome-back-min-days" />
            <span className="text-xs text-gray-500">days</span>
          </div>
        </Row>
        <Row label="Morning nudge — 9:00 AM Dubai" hint="Sent on the return day (the day after the leave ends)">
          <Switch checked={cfg.morning_enabled} disabled={readOnly || !cfg.enabled} onCheckedChange={(v) => save({ morning_enabled: v })} data-testid="welcome-back-morning" />
        </Row>
        <Row label="Clock-in nudge" hint="Sent when the employee first clocks in after the leave (within 7 days)">
          <Switch checked={cfg.clockin_enabled} disabled={readOnly || !cfg.enabled} onCheckedChange={(v) => save({ clockin_enabled: v })} data-testid="welcome-back-clockin" />
        </Row>
        {!readOnly && (
          <div className="pt-3">
            <Button size="sm" variant="outline" onClick={runNow} disabled={busy} data-testid="welcome-back-run-now">
              <Play className="h-3.5 w-3.5 mr-1" />{busy ? 'Sending…' : "Send today's morning nudges now"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
